import { useCallback, useEffect, useRef, useState } from 'react';
import Artplayer from 'artplayer';
import type Hls from 'hls.js';
import type { DistributionClient } from '../lib/api';
import type { IptvChannelDetail, IptvPlaybackPlan, IptvPlaybackMode } from '../lib/types';
import { useI18n } from '../lib/use-i18n';

/** Live playlists move; keep the buffer short so we stay near the edge. */
const LIVE_HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,
  backBufferLength: 10,
  maxBufferLength: 20,
  maxMaxBufferLength: 30,
  liveSyncDurationCount: 3,
  manifestLoadingMaxRetry: 4,
  levelLoadingMaxRetry: 4,
  fragLoadingMaxRetry: 6,
};

const CHROME_HIDE_MS = 5000;

function artTypeFor(mode: IptvPlaybackMode): string {
  if (mode === 'hls') return 'm3u8';
  if (mode === 'mpegts') return 'ts';
  return 'mp4';
}

function levelLabel(height: number, bitrate: number): string {
  if (height > 0) return `${height}p`;
  if (bitrate > 0) return `${Math.round(bitrate / 1000)} kbps`;
  return 'Source';
}

/** Populate Artplayer settings with HLS ladder when the playlist has multiple levels. */
function installHlsQuality(art: Artplayer, hls: Hls, Events: typeof Hls.Events) {
  hls.on(Events.MANIFEST_PARSED, () => {
    if (hls.levels.length < 2) return;
    try {
      art.setting.remove('iptv-quality');
    } catch {
      // first install
    }
    const levels = hls.levels.map((level, index) => ({
      html: levelLabel(level.height || 0, level.bitrate || 0),
      level: index,
    }));
    art.setting.add({
      name: 'iptv-quality',
      html: 'Quality',
      width: 220,
      tooltip: 'Auto',
      selector: [
        { html: 'Auto', default: true, level: -1 },
        ...levels.sort((a, b) => {
          const ha = hls.levels[a.level]?.height || 0;
          const hb = hls.levels[b.level]?.height || 0;
          return hb - ha;
        }),
      ],
      onSelect(item) {
        const level = Number((item as { level?: number }).level);
        hls.currentLevel = Number.isFinite(level) ? level : -1;
        return item.html;
      },
    });
  });
}

/**
 * Full-viewport IPTV player (Artplayer + hls.js / mpegts.js / remux).
 * One close control in the overlay; player bar has settings + single fullscreen.
 */
export function IptvWatch({ client, channelId, onClose }: {
  client: DistributionClient;
  channelId: string;
  onClose: () => void;
  onOpen?: (channelId: string) => void;
}) {
  const { t } = useI18n();
  const host = useRef<HTMLDivElement | null>(null);
  const artRef = useRef<Artplayer | null>(null);
  const reported = useRef(false);
  const retried = useRef(false);
  const hideTimer = useRef(0);

  const [detail, setDetail] = useState<IptvChannelDetail | null>(null);
  const [plan, setPlan] = useState<IptvPlaybackPlan | null>(null);
  const [status, setStatus] = useState<'loading' | 'playing' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);

  const report = useCallback((ok: boolean, reason?: string) => {
    if (reported.current) return;
    reported.current = true;
    void client.reportIptvPlayback(channelId, ok, reason)
      .then((result) => {
        if (ok || retried.current || !result?.mode) return;
        retried.current = true;
        setAttempt((value) => value + 1);
      })
      .catch(() => undefined);
  }, [channelId, client]);

  const showChrome = useCallback(() => {
    setChromeVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setChromeVisible(false), CHROME_HIDE_MS);
  }, []);

  const exit = useCallback((event?: { stopPropagation?: () => void; preventDefault?: () => void }) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    onClose();
  }, [onClose]);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    reported.current = false;
    setPlan(null);
    setStatus('loading');
    showChrome();

    void (async () => {
      try {
        const [channel, playback] = await Promise.all([
          client.iptvChannel(channelId),
          client.iptvPlay(channelId),
        ]);
        if (cancelled) return;
        setDetail(channel);
        setPlan(playback);
      } catch {
        if (!cancelled) setStatus('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [attempt, channelId, client, showChrome]);

  useEffect(() => {
    retried.current = false;
    setAttempt(0);
    setDetail(null);
  }, [channelId]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else showChrome();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, showChrome]);

  useEffect(() => {
    const container = host.current;
    if (!plan || !container) return;

    let disposed = false;
    const src = client.iptvStreamUrl(plan);
    const type = artTypeFor(plan.mode);

    const fail = (reason: string) => {
      if (disposed) return;
      setStatus('failed');
      report(false, reason);
    };
    const succeed = () => {
      if (disposed) return;
      setStatus('playing');
      report(true);
      showChrome();
    };

    Artplayer.CONTROL_HIDE_TIME = CHROME_HIDE_MS;

    const art = new Artplayer({
      container,
      url: src,
      type,
      isLive: true,
      autoplay: true,
      muted: false,
      autoSize: false,
      autoMini: false,
      screenshot: false,
      setting: true,
      loop: false,
      flip: false,
      playbackRate: false,
      aspectRatio: true,
      fullscreen: true,
      fullscreenWeb: false,
      pip: true,
      mutex: true,
      backdrop: true,
      playsInline: true,
      theme: '#E31937',
      lang: 'en',
      moreVideoAttr: {
        playsInline: true,
      },
      customType: {
        m3u8(video, url) {
          void (async () => {
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
              video.src = url;
              return;
            }
            const { default: Hls, Events, ErrorTypes } = await import('hls.js');
            if (disposed) return;
            if (!Hls.isSupported()) {
              fail('hls unsupported');
              return;
            }
            const hls = new Hls(LIVE_HLS_CONFIG);
            hls.attachMedia(video);
            hls.loadSource(url);
            installHlsQuality(art, hls, Events);
            hls.on(Events.ERROR, (_event, data) => {
              if (!data.fatal) return;
              if (data.type === ErrorTypes.NETWORK_ERROR) hls.startLoad();
              else if (data.type === ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
              else fail(`hls ${data.details}`);
            });
            art.on('destroy', () => {
              try { hls.destroy(); } catch { /* ignore */ }
            });
          })();
        },
        ts(video, url) {
          void (async () => {
            const mpegts = (await import('mpegts.js')).default;
            if (disposed) return;
            if (!mpegts.isSupported()) {
              fail('mpegts unsupported');
              return;
            }
            const player = mpegts.createPlayer(
              { type: 'mpegts', isLive: true, url },
              { enableWorker: true, liveBufferLatencyChasing: true, lazyLoad: false },
            );
            player.attachMediaElement(video);
            player.on(mpegts.Events.ERROR, (errType: string, detail: string) => {
              fail(`mpegts ${errType}/${detail}`);
            });
            player.load();
            void Promise.resolve(player.play()).catch(() => undefined);
            art.on('destroy', () => {
              try { player.destroy(); } catch { /* ignore */ }
            });
          })();
        },
      },
    });

    artRef.current = art;

    art.on('video:playing', succeed);
    art.on('video:waiting', () => {
      if (disposed || reported.current) return;
      setStatus('loading');
    });
    art.on('video:error', () => fail('element error'));
    art.on('hover', (state) => {
      if (state) showChrome();
      else setChromeVisible(false);
    });
    art.on('focus', showChrome);

    return () => {
      disposed = true;
      artRef.current = null;
      try {
        art.destroy(false);
      } catch {
        // Half-initialised engines can throw on destroy.
      }
    };
  }, [client, plan, report, showChrome]);

  const now = detail?.schedule?.[0] ?? null;
  const next = detail?.schedule?.[1] ?? null;

  return (
    <div
      className={`iptv-player ${chromeVisible ? 'chrome-on' : 'chrome-off'}`}
      role="dialog"
      aria-modal="true"
      aria-label={detail?.name || 'IPTV'}
      onMouseMove={showChrome}
      onPointerDown={showChrome}
      onTouchStart={showChrome}
    >
      <div ref={host} className="iptv-player-stage" />

      {status === 'loading' && (
        <div className="iptv-player-loading" aria-live="polite" aria-label={t('loading')}>
          <div className="iptv-player-spinner" />
          <span>{detail?.name || t('pleaseWait')}</span>
          <i className="iptv-player-loading-bar" />
        </div>
      )}

      {status === 'failed' && (
        <div className="iptv-player-error">
          <b>{t('iptvChannelFailed')}</b>
          <span>{retried.current ? t('iptvChannelFailedHint') : t('iptvSwitchingSource')}</span>
          <button type="button" data-focusable="true" className="primary-action" onClick={exit}>
            {t('close')}
          </button>
        </div>
      )}

      <div className={`iptv-player-chrome ${chromeVisible || status !== 'playing' ? 'visible' : ''}`}>
        <header className="iptv-player-top">
          <div className="iptv-player-identity">
            {detail?.logo ? <img src={detail.logo} alt="" /> : null}
            <div>
              <b>{detail?.name || '…'}</b>
              {now ? (
                <small>
                  <em>LIVE</em>
                  {now.title}
                </small>
              ) : (
                <small>{detail?.group ?? t('iptvNoGuide')}</small>
              )}
            </div>
          </div>
          <button
            type="button"
            className="iptv-player-close"
            data-focusable="true"
            onClick={exit}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label={t('close')}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {(now || next) && status === 'playing' && (
          <footer className="iptv-player-programme">
            {now ? (
              <div className="iptv-player-now">
                <span>{formatClock(now.startAt)}–{formatClock(now.endAt)}</span>
                <b>{now.title}</b>
                {now.progress > 0 && now.progress < 100 ? (
                  <i className="iptv-progress"><i style={{ width: `${now.progress}%` }} /></i>
                ) : null}
              </div>
            ) : null}
            {next ? (
              <div className="iptv-player-next">
                <span>{t('iptvNext')}</span>
                <b>{next.title}</b>
              </div>
            ) : null}
          </footer>
        )}
      </div>
    </div>
  );
}

function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
